import { inject, Injectable, OnDestroy } from '@angular/core';
import { SharepointIdRequest } from '@dave/types/dist/proto/sharepoint/main';
import { Actions, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { BehaviorSubject, combineLatest, Observable, of, Subscription } from 'rxjs';
import { catchError, map, switchMap, tap } from 'rxjs/operators';
import { SharepointObjectEntity } from '../../../../dave-data-module/entities/sharepoint-object.entity';
import { AppGatewayResponseHasError, AppGatewayService } from '../../../../dave-data-module/services/app-gateway.service';
import { State } from '../../../../dave-data-module/State';
import { BaseActionTypes } from '../../../../dave-data-module/State/actions/base.actions';

class SharepointGetObject {}

@Injectable({
    providedIn: 'root',
})
export class SharepointDataService implements OnDestroy {
    private store = inject(Store<State>);
    private api = inject(AppGatewayService);
    private actions$ = inject(Actions);

    private clearSub: Subscription;
    constructor() {
        this.clearSub = this.actions$.pipe(ofType(BaseActionTypes.InitStore)).subscribe(() => this.clearCache());
    }

    ngOnDestroy(): void {
        this.clearSub.unsubscribe();
    }
    get$(payload: SharepointIdRequest): Observable<SharepointObjectEntity> {
        // return of(new SharepointGetObjectEntity(payload.Id, 'test', true, new Date()));
        return this.api.Request$({ SharepointGetObject: payload }).pipe(
            tap((response) => {
                if (AppGatewayResponseHasError(response)) {
                    throw new Error('SharepointGetObject failed with errors:' + response.Errors);
                }
            }),
            map((response) => {
                if (Object.keys(response?.Errors || {}).length === 0) {
                    return SharepointObjectEntity.fromBackend(response.SharepointGetObject);
                } else {
                    throw response.Errors;
                }
            }),
            catchError((err) => {
                this.store.dispatch(BaseActionTypes.ErrorAction({ Payload: { ToasterMessage: 'Sharepoint Objekt abrufen fehlgeschlagen', Err: err } }));
                return of(null);
            }),
        );
    }
    getChildren$(payload: SharepointIdRequest): Observable<SharepointObjectEntity[]> {
        // return of([new SharepointGetObjectEntity(payload.Id+'child', 'test-child', true, new Date())]);

        return this.api.Request$({ SharepointGetObjectChildren: payload }).pipe(
            tap((response) => {
                if (AppGatewayResponseHasError(response)) {
                    throw new Error('SharepointGetObjectChildren failed with errors:' + response.Errors);
                }
            }),
            map((response) => {
                if (Object.keys(response?.Errors || {}).length === 0) {
                    return response.SharepointGetObjectChildren.Objects.map(SharepointObjectEntity.fromBackend);
                } else {
                    throw response.Errors;
                }
            }),
            catchError((err) => {
                this.store.dispatch(BaseActionTypes.ErrorAction({ Payload: { ToasterMessage: 'Sharepoint Objekte abrufen fehlgeschlagen', Err: err } }));
                return of(null);
            }),
        );
    }
    getParents$(payload: SharepointIdRequest): Observable<SharepointObjectEntity[]> {
        // return of([
        //     new SharepointGetObjectEntity(payload.Id.replace('child', ''), 'test 1', true, new Date()),
        //     new SharepointGetObjectEntity(payload.Id, 'test 2', true, new Date())
        // ]);
        return this.api.Request$({ SharepointGetObjectParents: payload }).pipe(
            tap((response) => {
                if (AppGatewayResponseHasError(response)) {
                    throw new Error('SharepointGetObjectParents failed with errors:' + response.Errors);
                }
            }),
            map((response) => {
                if (Object.keys(response?.Errors || {}).length === 0) {
                    return response.SharepointGetObjectParents.Objects.map(SharepointObjectEntity.fromBackend);
                } else {
                    throw response.Errors;
                }
            }),
            catchError((err) => {
                this.store.dispatch(BaseActionTypes.ErrorAction({ Payload: { ToasterMessage: 'Sharepoint Objekte abrufen fehlgeschlagen', Err: err } }));
                return of(null);
            }),
        );
    }

    public clearCache() {
        this.byIdCache.clear();
        this.folderAndParentCache.clear();
        this.byParentCache.clear();
    }
    byParentCache = new Map<string, BehaviorSubject<string[]>>();
    folderAndParentCache = new Map<string, BehaviorSubject<string[]>>();
    byIdCache = new Map<string, BehaviorSubject<SharepointObjectEntity>>();
    // reimplement required functions from folderDataService
    getFolderAndParentsById(id: string, useCache = true) {
        const response = () => this.folderAndParentCache.get(id).pipe(switchMap((ids) => (ids.length ? combineLatest(ids.map((id) => this.byIdCache.get(id))) : of([] as SharepointObjectEntity[]))));
        if (!useCache || !this.folderAndParentCache.has(id)) {
            return this.getParents$({ Id: id }).pipe(
                switchMap((r) => {
                    if (!r.some((o) => o.Id === id)) {
                        throw new Error('getFolderAndParentsById leaf missing');
                    }
                    r?.forEach((f) => this.setIdCache(f));
                    const folderIds = r?.filter((o) => o.IsFolder).map((o) => o.Id);
                    if (this.folderAndParentCache.has(id)) {
                        this.folderAndParentCache.get(id).next(folderIds);
                    } else {
                        this.folderAndParentCache.set(id, new BehaviorSubject<string[]>(folderIds));
                    }
                    return response();
                }),
            );
        }
        return response();
    }

    getFolderByParent(id: string, useCache = true) {
        const response = () => this.byParentCache.get(id).pipe(
            switchMap((ids) => (ids.length ? combineLatest(ids.map((id) => this.byIdCache.get(id))) : of([] as SharepointObjectEntity[]))),
            map(objects => objects.filter(o => o.IsFolder)),
        );
        if (!useCache || !this.byParentCache.has(id)) {
            return this.getChildren$({ Id: id }).pipe(
                switchMap((r) => {
                    r?.forEach((f) => this.setIdCache(f));
                    const folderIds = r?.map((o) => o.Id);
                    if (this.byParentCache.has(id)) {
                        this.byParentCache.get(id).next(folderIds);
                    } else {
                        this.byParentCache.set(id, new BehaviorSubject<string[]>(folderIds));
                    }
                    return response();
                }),
            );
        }
        return response();
    }
    getFolderById(id: string, useCache = true) {
        const response = () => this.byIdCache.get(id).pipe(
            map(folder => {
                if (!folder.IsFolder) {
                    throw new Error('Not a folder');
                }
                return folder;
            })
        );
        if (!useCache || !this.byIdCache.has(id)) {
            return this.get$({ Id: id }).pipe(
                switchMap((r) => {
                    this.setIdCache(r);
                    return response();
                }),
            );
        }
        return response();
    }

    private setIdCache(f: SharepointObjectEntity) {
        if (this.byIdCache.has(f.Id)) {
            this.byIdCache.get(f.Id).next(f);
        } else {
            this.byIdCache.set(f.Id, new BehaviorSubject<SharepointObjectEntity>(f));
        }
    }











    getFilesByParent(id: string, useCache = true) {
        const response = () => this.byParentCache.get(id).pipe(
            switchMap((ids) => (ids.length ? combineLatest(ids.map((id) => this.byIdCache.get(id))) : of([] as SharepointObjectEntity[]))),
            map(objects => objects.filter(o => !o.IsFolder)),
        );
        if (!useCache || !this.byParentCache.has(id)) {
            return this.getChildren$({ Id: id }).pipe(
                switchMap((r) => {
                    r?.forEach((f) => this.setIdCache(f));
                    const folderIds = r?.map((o) => o.Id);
                    if (this.byParentCache.has(id)) {
                        this.byParentCache.get(id).next(folderIds);
                    } else {
                        this.byParentCache.set(id, new BehaviorSubject<string[]>(folderIds));
                    }
                    return response();
                }),
            );
        }
        return response();
    }
}
